import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM, fmtRMSigned, fmtMonth } from "@/lib/format";
import { PeriodSelector, periodLabel, periodParams } from "@/components/PeriodSelector";
import { ArrowLeft, TrendingUp, Receipt, Equal, Minus } from "lucide-react";

export default function NetProfit() {
  const [params, setParams] = useSearchParams();
  const period = params.get("period") || "all";
  const value = params.get("value") || null;
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/net-profit", { params: periodParams(period, value) }).then((r) => setData(r.data));
  }, [period, value]);

  const pLabel = periodLabel(period, value);
  const onChange = (p, v) => setParams(v ? { period: p, value: v } : { period: p });

  return (
    <Layout>
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>

      <PageHeader
        title="Net Profit Breakdown"
        subtitle={`How net profit is calculated · ${pLabel}`}
        action={<PeriodSelector period={period} value={value} onChange={onChange} testid="netprofit-period-selector" />}
      />

      {/* Formula explainer */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 mb-6" data-testid="netprofit-formula">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-4">Calculation</div>
        <div className="flex items-stretch gap-3 flex-wrap">
          <div className="flex-1 min-w-[180px] rounded-lg border border-green-200 bg-green-50 p-4">
            <div className="flex items-center gap-2 text-green-700"><TrendingUp className="h-4 w-4" /><span className="text-xs font-semibold uppercase">Interest Income</span></div>
            <div className="font-mono-fig text-2xl font-bold text-green-700 mt-2">{fmtRM(data?.incomeCollected)}</div>
            <div className="text-xs text-green-600/70 mt-1">{data?.incomeCount || 0} interest payments collected</div>
          </div>
          <div className="flex items-center justify-center px-1"><Minus className="h-6 w-6 text-slate-400" /></div>
          <div className="flex-1 min-w-[180px] rounded-lg border border-rose-200 bg-rose-50 p-4">
            <div className="flex items-center gap-2 text-rose-700"><Receipt className="h-4 w-4" /><span className="text-xs font-semibold uppercase">Operating Expenses</span></div>
            <div className="font-mono-fig text-2xl font-bold text-rose-700 mt-2">{fmtRM(data?.totalExpenses)}</div>
            <div className="text-xs text-rose-600/70 mt-1">{data?.expenseCount || 0} expense entries</div>
          </div>
          <div className="flex items-center justify-center px-1"><Equal className="h-6 w-6 text-slate-400" /></div>
          <div className="flex-1 min-w-[180px] rounded-lg border border-slate-300 bg-slate-900 p-4">
            <div className="flex items-center gap-2 text-slate-300"><span className="text-xs font-semibold uppercase">Net Profit</span></div>
            <div className={`font-mono-fig text-2xl font-bold mt-2 ${data?.netProfit >= 0 ? "text-green-400" : "text-red-400"}`}>{fmtRMSigned(data?.netProfit)}</div>
            <div className="text-xs text-slate-400 mt-1">{pLabel}</div>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-4">
          Net Profit counts realised <b>interest income</b> actually collected, minus <b>operating expenses</b> (office, salary, petrol, etc.).
          Principal repayments are excluded (they return your capital, not profit), and loan commissions are tracked separately in the ledger.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly breakdown */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
          <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-heading font-semibold text-slate-900">Monthly Breakdown</h3></div>
          <table className="w-full text-sm" data-testid="netprofit-monthly-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-2.5 font-medium">Month</th>
                <th className="px-3 py-2.5 font-medium text-right">Income</th>
                <th className="px-3 py-2.5 font-medium text-right">Expenses</th>
                <th className="px-5 py-2.5 font-medium text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {data?.monthly.map((m) => (
                <tr key={m.month} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium text-slate-900">{fmtMonth(m.month)}</td>
                  <td className="px-3 py-3 text-right font-mono-fig text-green-600">{fmtRM(m.income)}</td>
                  <td className="px-3 py-3 text-right font-mono-fig text-rose-600">{fmtRM(m.expenses)}</td>
                  <td className={`px-5 py-3 text-right font-mono-fig font-semibold ${m.net >= 0 ? "text-slate-900" : "text-red-600"}`}>{fmtRMSigned(m.net)}</td>
                </tr>
              ))}
              {data && data.monthly.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">No data in this period.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Expense detail */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-900">Operating Expenses</h3>
            <span className="text-xs text-slate-400">{data?.expenseCount || 0} entries · {fmtRM(data?.totalExpenses)}</span>
          </div>
          <table className="w-full text-sm" data-testid="netprofit-expense-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-2.5 font-medium">Date</th>
                <th className="px-3 py-2.5 font-medium">Description</th>
                <th className="px-5 py-2.5 font-medium text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data?.expenses.map((e, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{e.date}</td>
                  <td className="px-3 py-3 text-slate-700">{e.description || "—"}</td>
                  <td className="px-5 py-3 text-right font-mono-fig text-rose-600">{fmtRM(e.amount)}</td>
                </tr>
              ))}
              {data && data.expenses.length === 0 && <tr><td colSpan={3} className="px-5 py-10 text-center text-slate-400">No operating expenses in this period.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
